import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { PointDisplay, SkeletonText } from 'utoniq-ui'

import { Maybe, PaymentDetailFragment } from '@/clients/utoniq-core/schema'
import { StorageImage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'

type PaymentInfoProps = {
  loading: boolean
  formattedPrepaidPointBalance: string | number
  paymentSource?: Maybe<PaymentDetailFragment> | undefined
  purchaseType?: string
  showNote?: boolean
}

export const PaymentInfo: Organism<PaymentInfoProps> = ({
  loading,
  formattedPrepaidPointBalance,
  paymentSource,
  purchaseType,
  showNote,
}) => {
  const { t } = useTranslation('storePurchaseOrderDetail')

  if (loading) return <PaymentInfoSkeleton />

  switch (purchaseType) {
    case 'konbini':
      return (
        <CellFrame sx={{ pb: 4 }}>
          <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
            {t('paymentInfo', '支払い情報')}
          </Typography>

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
              <Stack>
                <Typography variant="h6">
                  {t('convenienceStore', 'コンビニ決済')}
                </Typography>
                {showNote && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 2 }}
                  >
                    {t(
                      'convenienceStoreDesc',
                      'ファミリーマート、ローソン、ミニストップ、セイコーマートに対応しています。'
                    )}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </CellFrame>
      )
    case 'cash':
      return (
        <CellFrame sx={{ pb: 4 }}>
          <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
            {t('paymentInfo', '支払い情報')}
          </Typography>

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
              <Stack>
                {!paymentSource ? (
                  <Stack>
                    <Typography variant="h6">
                      {t('creditCard', 'クレジットカード')}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={3} alignItems="center">
                    <StorageImage
                      height={25}
                      src={generateCreditCardLogoPath(paymentSource?.brand)}
                      target="common"
                    />
                    <Typography variant="h6">
                      {t('last4Digits', { number: paymentSource?.last4 })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </CellFrame>
      )
    case 'prepaid_point':
      return (
        <CellFrame sx={{ pb: 4 }} color="text.secondary">
          <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
            {t('paymentInfo', '支払い情報')}
          </Typography>

          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
              <Stack>
                <Stack direction="row" mt={1} justifyContent="space-between">
                  <Stack spacing={1}>
                    <Typography variant="h6">
                      {t('purchaseByPoint', 'ポイントで購入')}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        {t('pointBalance', 'ポイント残高')}
                      </Typography>
                      <PointDisplay point={formattedPrepaidPointBalance} />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </CellFrame>
      )
    default:
      return null
  }
}

const PaymentInfoSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5 }}>
    <Box sx={{ py: 2 }}>
      <SkeletonText variant="h3" width="50%" />
    </Box>
    <Card elevation={0} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ px: 5, py: 6, '&:last-child': { pb: 6 } }}>
        <Skeleton variant="rectangular" height={32} width="50%" />
      </CardContent>
    </Card>
  </CellFrame>
)
